import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import { firstValueFrom } from 'rxjs';
import { filter, toArray } from 'rxjs/operators';
import {
  SOFTLINE_API_SALES_DOCUMENT,
  SOFTLINE_API_SALES_DOCUMENT_BY_ID,
  SOFTLINE_API_SALES_DOCUMENT_CLONE,
  SOFTLINE_API_SALES_DOCUMENT_DOWN_PAYMENT_CREDIT_NOTES,
  SOFTLINE_API_SALES_DOCUMENT_VALIDATE,
  SOFTLINE_API_SALES_DOCUMENT_FINALIZE,
  SOFTLINE_API_SALES_DOCUMENT_FINALIZE_STEPS,
  SOFTLINE_API_SALES_DOCUMENT_POSTPONE,
  SOFTLINE_API_SALES_DOCUMENT_TYPES,
  SOFTLINE_API_SALES_DOCUMENT_SIGNATURE,
  SOFTLINE_API_SALES_DOCUMENT_TYPES_FOR_DOCUMENT,
} from '../sales.api';

export type FinalizeStep =
  | 'selectConstructionSite'
  | 'enterExternalPurchaseOrderNumber'
  | 'enterManualAddress'
  | 'selectDeliveryDateRange'
  | 'selectCollector'
  | 'pay'
  | 'sign'
  | 'creditLimit';
import { CloneDocumentRequest } from '../types/clone-document-request';
import { CreateOrUpdateSalesDocumentRequest } from '../types/create-or-update-sales-document-request';
import { DocumentType } from '../types/document-type';
import { SalesDocument } from '../types/sales-document';
import { SalesDocumentValidation } from '../types/sales-document-validation';

export interface FinalizeSalesDocumentResult {
  documentNumber: string;
  key: string;
  signatureRequired: boolean;
}

@Injectable()
export class SalesDocumentRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  create(body: CreateOrUpdateSalesDocumentRequest): Promise<SalesDocument> {
    return this.dataAccess.create<CreateOrUpdateSalesDocumentRequest, SalesDocument>(
      { path: SOFTLINE_API_SALES_DOCUMENT },
      body
    );
  }

  load(id: string): Promise<SalesDocument> {
    return this.dataAccess.get<SalesDocument>({
      path: SOFTLINE_API_SALES_DOCUMENT_BY_ID,
      pathParams: { id },
    });
  }

  update(
    id: string,
    body: Partial<CreateOrUpdateSalesDocumentRequest>
  ): Promise<SalesDocument> {
    return this.dataAccess.patch<
      Partial<CreateOrUpdateSalesDocumentRequest>,
      SalesDocument
    >({ path: SOFTLINE_API_SALES_DOCUMENT_BY_ID, pathParams: { id } }, body);
  }

  /** Copies `sourceId` into an independent new document of the same Belegart.
   *  `itemIds` limits the copy to those positions (each with its whole
   *  subtree); omitted = all positions, empty = Kopfdaten only. Returns the new
   *  document complete. */
  clone(body: CloneDocumentRequest): Promise<SalesDocument> {
    return this.dataAccess.patch<CloneDocumentRequest, SalesDocument>(
      { path: SOFTLINE_API_SALES_DOCUMENT_CLONE },
      body
    );
  }

  /** Creates the Anzahlungsgutschrift for an Auftrag. The document id travels in
   *  the path and an empty body is valid; the response is the SOURCE Auftrag
   *  carrying a new negative downPaymentEntry, not the created Gutschrift.
   *  TODO: confirm whether a body can select individual Anzahlungen — Ticket
   *  452.648 names only path and verb. */
  createDownPaymentCreditNote(id: string): Promise<SalesDocument> {
    return this.dataAccess.create<Record<string, never>, SalesDocument>(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_DOWN_PAYMENT_CREDIT_NOTES,
        pathParams: { id },
      },
      {}
    );
  }

  validate(id: string): Promise<SalesDocumentValidation> {
    return this.dataAccess.create<{ preview: boolean }, SalesDocumentValidation>(
      { path: SOFTLINE_API_SALES_DOCUMENT_VALIDATE, pathParams: { id } },
      { preview: true }
    );
  }

  delete(id: string): Promise<void> {
    return this.dataAccess.delete<void>({
      path: SOFTLINE_API_SALES_DOCUMENT_BY_ID,
      pathParams: { id },
    });
  }

  getDocumentTypes(customerId?: string): Promise<DocumentType[]> {
    return this.dataAccess.get<DocumentType[]>({
      path: SOFTLINE_API_SALES_DOCUMENT_TYPES,
      queryParams: customerId ? { customerId } : undefined,
    });
  }

  postpone(id: string): Promise<SalesDocument> {
    return this.dataAccess.create<unknown, SalesDocument>(
      { path: SOFTLINE_API_SALES_DOCUMENT_POSTPONE, pathParams: { id } },
      {}
    );
  }

  finalize(id: string): Promise<FinalizeSalesDocumentResult> {
    return this.dataAccess.create<unknown, FinalizeSalesDocumentResult>(
      { path: SOFTLINE_API_SALES_DOCUMENT_FINALIZE, pathParams: { id } },
      {}
    );
  }

  signDocument(id: string, signature: Blob, signerName?: string | null): Promise<void> {
    const file = new File([signature], 'signature.png', {
      type: signature.type || 'image/png',
    });
    const body: { file: File; signerName?: string } = { file };
    const name = signerName?.trim();
    if (name) body.signerName = name;
    return firstValueFrom(
      this.dataAccess
        .upload<typeof body, void>(
          { path: SOFTLINE_API_SALES_DOCUMENT_SIGNATURE, pathParams: { id } },
          body
        )
        .pipe(filter((event) => event?.type === 'response'))
    ).then(() => undefined);
  }

  async loadSignature(id: string): Promise<Blob | null> {
    const events = await firstValueFrom(
      this.dataAccess
        .download({ path: SOFTLINE_API_SALES_DOCUMENT_SIGNATURE, pathParams: { id } })
        .pipe(toArray())
    );
    const response = events.find((event) => event?.type === 'response');
    const blob = (response as { response?: Blob | ArrayBuffer } | undefined)?.response;
    return blob instanceof Blob ? blob : null;
  }

  getDocumentTypesForDocument(id: string): Promise<DocumentType[]> {
    return this.dataAccess.get<DocumentType[]>({
      path: SOFTLINE_API_SALES_DOCUMENT_TYPES_FOR_DOCUMENT,
      pathParams: { id },
    });
  }

  getFinalizeSteps(id: string): Promise<FinalizeStep[]> {
    return this.dataAccess.get<FinalizeStep[]>({
      path: SOFTLINE_API_SALES_DOCUMENT_FINALIZE_STEPS,
      pathParams: { id },
    });
  }
}
